import clsx from 'clsx';
import { LockClosedIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import { formatDateSafe } from '@/utils/dateUtils';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import type { HolidayEntry } from '../../../models/holiday.types';
import {
    TABLE_COMPACT,
    TABLE_CONTAINER,
    TABLE_MOBILE_MEDIA_QUERY,
    TABLE_SCROLL,
    THEAD,
    TH,
    TR,
    TD,
} from '../../vacationTable.styles';
import {
    COLUMN_LABELS,
    EDIT_LABEL,
    RECURRING_NO,
    RECURRING_NO_HINT,
    RECURRING_YES,
    REMOVE_LABEL,
    LOCKED_BADGE,
    LOCKED_HINT,
    SCOPE_CLASSES,
} from '../holidayManager.definitions';
import { HolidayCard } from './HolidayCard';

interface HolidayTableProps {
    holidays: HolidayEntry[];
    canEdit: boolean;
    onEdit: (holiday: HolidayEntry) => void;
    onRemove: (holiday: HolidayEntry) => void;
}

export function HolidayTable({ holidays, canEdit, onEdit, onRemove }: HolidayTableProps) {
    const isMobile = useMediaQuery(TABLE_MOBILE_MEDIA_QUERY);

    if (isMobile) {
        return (
            <div className="space-y-3">
                {holidays.map((holiday) => (
                    <HolidayCard
                        key={holiday.id}
                        holiday={holiday}
                        canEdit={canEdit}
                        onEdit={onEdit}
                        onRemove={onRemove}
                    />
                ))}
            </div>
        );
    }

    return (
        <div className={TABLE_CONTAINER}>
            <div className={TABLE_SCROLL}>
            <table className={TABLE_COMPACT}>
                <colgroup>
                    <col style={{ width: '130px' }} />
                    <col />
                    <col style={{ width: '140px' }} />
                    <col style={{ width: '170px' }} />
                    {canEdit && <col style={{ width: '130px' }} />}
                </colgroup>
                <thead className={THEAD}>
                    <tr>
                        {[COLUMN_LABELS.date, COLUMN_LABELS.name, COLUMN_LABELS.scope, COLUMN_LABELS.recurring].map(
                            (label) => (
                                <th key={label} scope="col" className={TH}>
                                    {label}
                                </th>
                            )
                        )}
                        {canEdit && (
                            <th scope="col" className={`${TH} text-right`}>
                                {COLUMN_LABELS.actions}
                            </th>
                        )}
                    </tr>
                </thead>

                <tbody>
                    {holidays.map((holiday) => (
                        <tr key={holiday.id} className={TR}>
                            <td className={`${TD} whitespace-nowrap tabular-nums font-medium text-raro-gray-900`}>
                                {formatDateSafe(holiday.date)}
                            </td>
                            <td className={`${TD} font-medium`}>{holiday.name}</td>
                            <td className={TD}>
                                <span
                                    className={clsx(
                                        'inline-flex items-center rounded-md px-2 py-1 text-xs font-medium font-montserrat',
                                        SCOPE_CLASSES[holiday.scope]
                                    )}
                                >
                                    {holiday.scope}
                                </span>
                            </td>
                            <td className={TD}>
                                {holiday.recurring ? (
                                    <span className="text-raro-gray-700">{RECURRING_YES}</span>
                                ) : (
                                    <span className="text-amber-700" title={RECURRING_NO_HINT}>
                                        {RECURRING_NO}
                                    </span>
                                )}
                            </td>
                            {canEdit && (
                                <td className={`${TD} whitespace-nowrap text-right`}>
                                    {holiday.locked ? (
                                        <span
                                            title={LOCKED_HINT}
                                            aria-label={LOCKED_HINT}
                                            className="inline-flex items-center gap-1 text-xs text-raro-gray-400 font-montserrat"
                                        >
                                            <LockClosedIcon
                                                className="h-3.5 w-3.5"
                                                aria-hidden="true"
                                            />
                                            {LOCKED_BADGE[holiday.scope]}
                                        </span>
                                    ) : (
                                    <>
                                    <button
                                        type="button"
                                        onClick={() => onEdit(holiday)}
                                        aria-label={`${EDIT_LABEL} ${holiday.name}`}
                                        className="rounded-lg p-1.5 text-primary-700 transition-colors hover:bg-primary-50"
                                    >
                                        <PencilSquareIcon className="h-4 w-4" aria-hidden="true" />
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => onRemove(holiday)}
                                        aria-label={`${REMOVE_LABEL} ${holiday.name}`}
                                        className="ml-1 rounded-lg p-1.5 text-red-600 transition-colors hover:bg-red-50"
                                    >
                                        <TrashIcon className="h-4 w-4" aria-hidden="true" />
                                    </button>
                                    </>
                                    )}
                                </td>
                            )}
                        </tr>
                    ))}
                </tbody>
            </table>
            </div>
        </div>
    );
}

export default HolidayTable;
